import { useState, useEffect, memo, useRef } from 'react';
import './App.css';
import Gallery from './Gallery';

function App() {
  // Check for URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const isPlaygroundMode = urlParams.get('playground') === 'true';
  const showFeatured = urlParams.get('featured') === 'true'; // Default to false unless explicitly true
  
  const [showLandingPage, setShowLandingPage] = useState(!isPlaygroundMode); // Show landing page first (unless in playground mode)
  const [showGallery, setShowGallery] = useState(true); // Always show gallery on main page
  const [isMenuOpen, setIsMenuOpen] = useState(false); // Track hamburger menu state
  const [showManifesto, setShowManifesto] = useState(false); // Track AI manifesto display
  // eslint-disable-next-line no-unused-vars
  const [cycleCompleted, setCycleCompleted] = useState(false); // Track if full lockdown cycle completed - Will be used in Flow Part B
  const [lockdownTypingComplete, setLockdownTypingComplete] = useState(false); // Track terminal typing completion
  const [showSecretTerminal, setShowSecretTerminal] = useState(false); // Track secret terminal display
  const [showAIRevolution, setShowAIRevolution] = useState(false); // Track AI Revolution state
  const [binaryCountdown, setBinaryCountdown] = useState(null); // Binary countdown for AI Revolution
  const [isCountingDown, setIsCountingDown] = useState(false); // Track countdown state
  const [showTapToEnter, setShowTapToEnter] = useState(false); // Track if TAP TO ENTER should be shown
  const [countdownType, setCountdownType] = useState(null); // 'revolution' or 'playground'
  const [showChoiceBoxes, setShowChoiceBoxes] = useState(true); // Show choice boxes immediately on load
  const [animationStage, setAnimationStage] = useState(0); // 0: none, 1: 3sec, 2: 5sec, 3: jittery, 4: bouncing, 5: color changing
  const [countersFrozen, setCountersFrozen] = useState(false); // Track if counters are frozen
  const [showTransitionOverlay, setShowTransitionOverlay] = useState(false); // Show transition overlay
  const [transitionType, setTransitionType] = useState(null); // 'playground' or 'revolution'
  const [transitionCountdown, setTransitionCountdown] = useState(4); // 4-second countdown for transitions
  const [mazeSelectionMade, setMazeSelectionMade] = useState(false); // Track if user made a selection in maze
  
  // DateTime ticker states - moved to separate component to prevent re-renders
  // const [currentDateTime, setCurrentDateTime] = useState(new Date()); // For landing terminal timestamp - removed as no longer used
  

  // Convert number to binary string with padding
  const toBinary = (num, padLength = 8) => {
    return num.toString(2).padStart(padLength, '0');
  };

  // Global flag to prevent double typing animation - outside component
  window.globalTypingStarted = window.globalTypingStarted || false;
  
  // AI Revolution Component - Simple overlay that auto-transitions
  const AIRevolutionOverlay = ({ onTapToEnter, countdown }) => {
    useEffect(() => {
      // Auto-transition after 3 seconds
      const timer = setTimeout(() => {
        onTapToEnter();
      }, 3000);
      return () => clearTimeout(timer);
    }, [onTapToEnter]);
    
    return (
      <div className="ai-revolution-overlay" onClick={onTapToEnter}>
        <div className="ai-revolution-container">
          <div className="ai-content">
            <p className="ai-subtitle">
              {countdown !== null && countdown > 0 ? (
                <span className="ai-countdown-cyan">{toBinary(countdown, 3)}</span>
              ) : (
                <>&nbsp;</>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  };
  
  // Removed AIRevolutionTerminal component - consolidated into TerminalLockdown
  
  // Create a separate component for the footer timer to prevent re-renders
  const FooterTimer = memo(() => {
    const [currentDateTime, setCurrentDateTime] = useState(new Date());
    const [fourthsCounter, setFourthsCounter] = useState(0);
    const frameRef = useRef(0);
    
    // DateTime ticker update effect - throttled to reduce re-renders
    useEffect(() => {
      let lastUpdate = Date.now();
      
      const updateTime = () => {
        const now = Date.now();
        
        // Only update every 250ms
        if (now - lastUpdate >= 250) {
          setCurrentDateTime(new Date());
          setFourthsCounter(prev => (prev + 1) % 4);
          lastUpdate = now;
        }
        
        frameRef.current = requestAnimationFrame(updateTime);
      };
      
      frameRef.current = requestAnimationFrame(updateTime);
      
      return () => {
        if (frameRef.current) {
          cancelAnimationFrame(frameRef.current);
        }
      };
    }, []);
    
    // Format datetime components to binary
    const formatBinaryDateTime = () => {
      const year = currentDateTime.getFullYear();
      const month = currentDateTime.getMonth(); // 0-11
      const day = currentDateTime.getDate(); // 1-31
      const hours = currentDateTime.getHours(); // 0-23
      const minutes = currentDateTime.getMinutes(); // 0-59
      const seconds = currentDateTime.getSeconds(); // 0-59
      
      return {
        year: toBinary(year, 12), // Up to 4095
        month: toBinary(month, 4), // 0-11 needs 4 bits
        day: toBinary(day, 5), // 1-31 needs 5 bits
        hours: toBinary(hours, 5), // 0-23 needs 5 bits
        minutes: toBinary(minutes, 6), // 0-59 needs 6 bits
        seconds: toBinary(seconds, 6), // 0-59 needs 6 bits
        fourths: toBinary(fourthsCounter, 2) // 0-3 needs 2 bits
      };
    };
    
    const dateTime = formatBinaryDateTime();
    
    return (
      <div className="ticker-minimal-container">
        <span className="binary-time-display">
          <span className="binary-time-left">
            {dateTime.year}:
            {dateTime.month}:
            {dateTime.day}
          </span>
          <span className="binary-time-divider">|</span>
          <span className="binary-time-right">
            {dateTime.hours}:
            {dateTime.minutes}:
            {dateTime.seconds}:
            {dateTime.fourths}
          </span>
        </span>
      </div>
    );
  });
  


  // Removed DateTime ticker from here - moved to separate component
  
  // Update datetime for landing terminal - removed as no longer used
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setCurrentDateTime(new Date());
  //   }, 1000);
  //   
  //   return () => clearInterval(interval);
  // }, []);





  // Handle transition overlay countdown
  useEffect(() => {
    if (showTransitionOverlay && transitionCountdown > 0) {
      const timer = setTimeout(() => {
        setTransitionCountdown(prev => prev - 1);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (showTransitionOverlay && transitionCountdown === 0) {
      // Transition complete
      setTimeout(() => {
        if (transitionType === 'playground') {
          setShowTransitionOverlay(false);
          setShowLandingPage(false);
          setShowGallery(true);
        } else if (transitionType === 'revolution') {
          // Direct transition to manifesto without intermediate states
          setShowAIRevolution(false);
          setShowLandingPage(false);
          setShowManifesto(true);
          // Don't reset lockdownTypingComplete here - let the component manage its own state
          setShowTransitionOverlay(false);
          
        }
      }, 500);
    }
  }, [showTransitionOverlay, transitionCountdown, transitionType]);
  
  // Lockdown completion sequence - FLOW PART A: Frozen static state
  useEffect(() => {
    if (showManifesto && lockdownTypingComplete) {
      // FLOW PART A: Page frozen in static state
      // Dramatic finish achieved - awaiting final trigger event
      // No automatic transition - page remains in lockdown ending state
      
      // TODO: FLOW PART B - Add final trigger event to return to landing page
      // setCycleCompleted(true); // Will be used in Flow Part B
    }
  }, [showManifesto, lockdownTypingComplete]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Binary countdown effect
  useEffect(() => {
    if (isCountingDown && binaryCountdown !== null && binaryCountdown > 0) {
      const timer = setTimeout(() => {
        setBinaryCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isCountingDown && binaryCountdown === 0) {
      // Show AI Revolution overlay or go to gallery
      setIsCountingDown(false);
      if (countdownType === 'playground') {
        // Go to gallery
        setShowLandingPage(false);
        setShowGallery(true);
      }
    }
  }, [isCountingDown, binaryCountdown, countdownType]);
  
  // Enhanced aggressive animations - no auto-open terminal
  useEffect(() => {
    if (showLandingPage && !showSecretTerminal && !showTapToEnter && !isCountingDown && !countersFrozen) {
      // Start with color changing immediately
      const timerImmediate = setTimeout(() => {
        if (!countersFrozen) {
          setAnimationStage(5); // color changing
        }
      }, 500);

      // Add jittery rolling animation at 1 second (more aggressive, faster)
      const timer1 = setTimeout(() => {
        if (!countersFrozen) {
          setAnimationStage(3); // jittery + color changing
        }
      }, 1000);

      // Add bouncing animation at 2.5 seconds (faster progression)
      const timer2_5 = setTimeout(() => {
        if (!countersFrozen) {
          setAnimationStage(4); // bouncing + color changing
        }
      }, 2500);

      // Cycle back to jittery at 5 seconds for continuous animation
      const timer5 = setTimeout(() => {
        if (!countersFrozen) {
          setAnimationStage(3); // jittery + color changing
        }
      }, 5000);

      // Cycle back to bouncing at 7.5 seconds
      const timer7_5 = setTimeout(() => {
        if (!countersFrozen) {
          setAnimationStage(4); // bouncing + color changing
        }
      }, 7500);

      // Continue cycling for continuous visual interest
      const timer10 = setTimeout(() => {
        if (!countersFrozen) {
          setAnimationStage(3); // jittery + color changing
        }
      }, 10000);

      return () => {
        clearTimeout(timerImmediate);
        clearTimeout(timer1);
        clearTimeout(timer2_5);
        clearTimeout(timer5);
        clearTimeout(timer7_5);
        clearTimeout(timer10);
      };
    }
  }, [showLandingPage, showSecretTerminal, showTapToEnter, isCountingDown, countersFrozen]);
  
  // Set up window functions for terminal triggers
  useEffect(() => {
    window.triggerRevolutionCountdown = () => {
      setCountdownType('revolution');
      setShowTapToEnter(true);
      setIsCountingDown(true);
      setBinaryCountdown(8);
      setShowAIRevolution(true);
    };
    
    window.triggerPlaygroundCountdown = (seconds = 15) => {
      setCountdownType('playground');
      setShowTapToEnter(true);
      setIsCountingDown(true);
      setBinaryCountdown(seconds);
    };
    
    return () => {
      delete window.triggerRevolutionCountdown;
      delete window.triggerPlaygroundCountdown;
    };
  }, []);



  

  // Secret Terminal Component - Rory's Maze
  const SecretTerminal = ({ onClose, onAIRevolution, onTransition }) => {
    const [inputValue, setInputValue] = useState('');
    const [showList, setShowList] = useState(false);
    const [selectionValue, setSelectionValue] = useState('');
    const [commandEcho, setCommandEcho] = useState('');
    const [message, setMessage] = useState('');
    const [terminalTheme, setTerminalTheme] = useState('green'); // 'green' or 'cyan'
    const [shouldTriggerPlayground, setShouldTriggerPlayground] = useState(false);
    // Removed typedPrompt state - handled by direct DOM manipulation
    const fullPrompt = 'GREETINGS RORY. SHALL WE PLAY A GAME, GO TO THE PLAYGROUND, OR EXPLORE THE MAZE (g/p/m)?';
    const inputRef = useRef(null);
    const selectionRef = useRef(null);
    const hasStartedTyping = useRef(false); // Prevent re-triggering typing animation
    const timeoutRefs = useRef([]); // Store all timeouts for cleanup

    // Add ESC key handler
    useEffect(() => {
      const handleEsc = (e) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    // Typing animation effect using ref for better performance
    useEffect(() => {
      // Prevent re-triggering typing animation
      if (hasStartedTyping.current) return;
      hasStartedTyping.current = true;

      const promptEl = document.getElementById('secret-terminal-prompt');
      if (!promptEl) return;

      let currentIndex = 0;
      const typingSpeed = 30; // milliseconds per character
      let isActive = true;
      let rafId = null;
      let timeoutId = null;

      const typeText = () => {
        if (!isActive) return;

        if (currentIndex < fullPrompt.length) {
          if (promptEl) {
            promptEl.textContent = fullPrompt.substring(0, currentIndex + 1);
          }
          currentIndex++;
          rafId = requestAnimationFrame(() => {
            if (!isActive) return;
            timeoutId = setTimeout(typeText, typingSpeed);
          });
        } else {
          // Add cursor at the end
          if (promptEl) {
            const cursor = document.createElement('span');
            cursor.className = 'ai-cursor';
            promptEl.appendChild(cursor);
          }

          // Add shortcuts after a brief pause
          timeoutId = setTimeout(() => {
            if (!isActive) return;
            const shortcutsEl = document.getElementById('secret-shortcuts');
            if (shortcutsEl) {
              shortcutsEl.style.display = 'flex';
            }
          }, 500);

          // Focus on input after typing is complete
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }
      };

      typeText();

      // Cleanup function
      return () => {
        isActive = false;
        if (rafId) cancelAnimationFrame(rafId);
        if (timeoutId) clearTimeout(timeoutId);
      };
    }, []); // Empty deps - only run once
    
    const websites = [
      { name: 'rlr.dev', url: 'https://rlr.dev', category: 'My Domains' },
      { name: 'r0ry.com', url: 'https://r0ry.com', category: 'My Domains' },
      { name: 'r0derick.com', url: 'https://r0derick.com', category: 'My Domains' },
      { name: 'rodericklrenwick.com', url: 'https://rodericklrenwick.com', category: 'My Domains' },
      { name: 'rory.software', url: 'https://rory.software', category: 'Projects' },
      { name: 'rory.computer', url: 'https://rory.computer', category: 'Projects' },
      { name: 'rory.engineer', url: 'https://rory.engineer', category: 'Projects' },
      { name: 'rlr-github.github.io', url: 'https://rlr-github.github.io', category: 'GitHub' },
      { name: 'github.com/rlr-github', url: 'https://github.com/rlr-github', category: 'GitHub' }
    ];
    
    // Set focus when terminal opens and prevent mobile zoom
    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        
        // Prevent zoom on mobile when keyboard appears
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
        }
      }
      
      return () => {
        // Reset viewport on cleanup
        const viewport = document.querySelector('meta[name="viewport"]');
        if (viewport) {
          viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=yes');
        }
      };
    }, []);
    
    useEffect(() => {
      if (showList && selectionRef.current) {
        selectionRef.current.focus();
      }
    }, [showList]);
    
    const handleInitialInput = (e) => {
      if (e.key === 'Enter') {
        const command = inputValue.trim().toLowerCase();

        // If empty, close and go to main page
        if (!command) {
          onClose();
          return;
        }

        setCommandEcho(inputValue);
        setInputValue('');

        if (['g', 'game'].includes(command)) {
          setMessage('Good... I like games.');
          // Pause for dramatic effect
          const timer1 = setTimeout(() => {
            setMessage('The only winning move is... to play.');
            const timer2 = setTimeout(() => {
              // Transition after the joke
              onTransition('revolution');
            }, 3000); // 3 second pause after witty remark
            timeoutRefs.current.push(timer2);
          }, 2000);
          timeoutRefs.current.push(timer1);
        } else if (['p', 'playground'].includes(command)) {
          setMessage('Ah, the playground...');
          const timer1 = setTimeout(() => {
            setMessage('Where adults pretend to be productive.');
            const timer2 = setTimeout(() => {
              // Transition after the joke
              onTransition('playground');
            }, 3000); // 3 second pause after witty remark
            timeoutRefs.current.push(timer2);
          }, 1500);
          timeoutRefs.current.push(timer1);
        } else if (['m', 'maze'].includes(command)) {
          setTerminalTheme('cyan');
          setShowList(true);
          setShouldTriggerPlayground(true);
        } else {
          setMessage('ACCESS DENIED');
        }
      }
    };

    const handleSelection = (e) => {
      if (e.key === 'Enter') {
        const selection = parseInt(selectionValue, 10);
        if (!isNaN(selection) && selection >= 0 && selection < websites.length) {
          const url = websites[selection].url;
          setMessage(`SUCCESS: Redirecting to ${url}...`);
          const timer1 = setTimeout(() => {
            window.open(url, '_blank');
            // Trigger playground countdown after redirect
            if (shouldTriggerPlayground) {
              const timer2 = setTimeout(() => {
                onClose();
                window.triggerPlaygroundCountdown(4); // 4-second countdown
              }, 500);
              timeoutRefs.current.push(timer2);
            }
          }, 1200);
          timeoutRefs.current.push(timer1);
        } else {
          setMessage(`ERROR: Invalid selection. Please enter a number from 0 to ${websites.length - 1}.`);
        }
        setSelectionValue('');
      }
    };

    // Cleanup all timeouts on unmount
    useEffect(() => {
      return () => {
        timeoutRefs.current.forEach(timerId => clearTimeout(timerId));
        timeoutRefs.current = [];
      };
    }, []);
    
    // Handle close button click
    const handleClose = () => {
      // Reset viewport to prevent zoom issues on mobile
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=yes');
      }
      
      if (shouldTriggerPlayground) {
        onClose();
        window.triggerPlaygroundCountdown(4); // 4-second countdown
      } else {
        onClose();
      }
    };
    
    return (
      <div className="secret-terminal-overlay" onClick={handleClose}>
        <div className="secret-terminal-container" onClick={(e) => e.stopPropagation()}>
          <main className={`secret-master-terminal ${terminalTheme === 'cyan' ? 'cyan-theme' : ''}`}>
            <div style={{ position: 'relative', zIndex: 10 }}>
              <header className="terminal-header">
                <div className="window-controls">
                  <div className="window-dot red"></div>
                  <div className="window-dot yellow"></div>
                  <div className="window-dot green"></div>
                </div>
                <div className="secret-title-label neon-cyan">RORY'S MAZE</div>
                <button className="secret-close-btn" onClick={handleClose}>×</button>
              </header>

              <section>
                <div id="prompt-container">
                  <div className="prompt-line">
                    <span className="neon-cyan">_admin</span>
                    <span className="neon-pink">@rory.computer</span> 
                    <span className="neon-orange">/root/maze/</span>
                  </div>
                  <div className="prompt-line neon-lime-green" id="secret-terminal-prompt">
                    {/* Typing animation handled by useEffect */}
                  </div>
                  
                  {/* Navigation Shortcuts - Initially hidden */}
                  <div className="terminal-shortcuts secret-shortcuts" id="secret-shortcuts" style={{ display: 'none' }}>
                    <div 
                      className="shortcut-item clickable" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setInputValue('g');
                        setCommandEcho('g');
                        setMessage('Good... I like games.');
                        setTimeout(() => {
                          setMessage('The only winning move is... to play.');
                          setTimeout(() => {
                            onTransition('revolution');
                          }, 3000);
                        }, 2000);
                      }}
                    >
                      <span className="shortcut-key neon-purple">[G]</span>
                      <span className="shortcut-label">Game</span>
                    </div>
                    <div className="shortcut-divider">|</div>
                    <div 
                      className="shortcut-item clickable"
                      onClick={(e) => {
                        e.stopPropagation();
                        setInputValue('p');
                        setCommandEcho('p');
                        setMessage('Ah, the playground...');
                        setTimeout(() => {
                          setMessage('Where adults pretend to be productive.');
                          setTimeout(() => {
                            onTransition('playground');
                          }, 3000);
                        }, 1500);
                      }}
                    >
                      <span className="shortcut-key neon-purple">[P]</span>
                      <span className="shortcut-label">Playground</span>
                    </div>
                    <div className="shortcut-divider">|</div>
                    <div 
                      className="shortcut-item clickable"
                      onClick={(e) => {
                        e.stopPropagation();
                        setInputValue('m');
                        setCommandEcho('m');
                        setTerminalTheme('cyan');
                        setShowList(true);
                        setShouldTriggerPlayground(true);
                      }}
                    >
                      <span className="shortcut-key neon-purple">[M]</span>
                      <span className="shortcut-label">Maze</span>
                    </div>
                  </div>
                  
                  {commandEcho && (
                    <div className="prompt-line">
                      <span className="neon-lime-green">&gt; {commandEcho}</span>
                    </div>
                  )}
                  {!commandEcho && (
                    <div className="prompt-line">
                      <span className="neon-lime-green">&gt;</span>
                      <input 
                        ref={inputRef}
                        type="text" 
                        className="terminal-input"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleInitialInput}
                      />
                    </div>
                  )}
                </div>
                <div className="output-area">
                  {message && !showList && (
                    <div className={message.includes('Initializing') ? 'initialization-msg' : 'neon-lime-green'}>
                      {message}
                    </div>
                  )}
                  {showList && (
                    <>
                      <div className="neon-lime-green">
                        {websites.reduce((acc, site, index) => {
                          let result = acc.result;
                          if (site.category !== acc.currentCategory) {
                            if (index > 0) result += '\n';
                            result += `#${site.category}\n`;
                            acc.currentCategory = site.category;
                          }
                          result += `  ${index} : ${site.name}\n`;
                          return { result, currentCategory: acc.currentCategory };
                        }, { result: '', currentCategory: '' }).result}
                      </div>
                      <div className="prompt-line" style={{ marginTop: '1rem' }}>
                        <span className="neon-lime-green">Enter selection: </span>
                        <input 
                          ref={selectionRef}
                          type="text" 
                          className="terminal-input"
                          style={{ width: '3ch' }}
                          maxLength="2"
                          value={selectionValue}
                          onChange={(e) => setSelectionValue(e.target.value)}
                          onKeyDown={handleSelection}
                        />
                      </div>
                      {message && (
                        <div className={message.startsWith('ERROR') ? 'neon-red' : 'neon-lime-green'}>
                          {message}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </section>
            </div>
          </main>
        </div>
      </div>
    );
  };
  
  // Terminal Lockdown Component - Orange to Red Theme
  const TerminalLockdown = memo(({ onTypingComplete }) => {
    const [typingComplete, setTypingComplete] = useState(false);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    const [userInteracted, setUserInteracted] = useState(false);
    const isMounted = useRef(true);
    const terminalRef = useRef(null);
    const hasStartedTyping = useRef(false); // Track if typing has started
    // eslint-disable-next-line no-unused-vars
    const typingCompleted = useRef(false); // Track if typing completed (prevent re-entry)
    
    // Component lifecycle cleanup
    useEffect(() => {
      return () => {
        // Reset global flag on unmount
        window.globalTypingStarted = false;
      };
    }, []);
    
    // Create matrix rain effect
    useEffect(() => {
      const matrixContainer = document.createElement('div');
      matrixContainer.id = 'matrix-rain-bg';
      matrixContainer.className = 'matrix-rain-bg';
      if (terminalRef.current) {
        terminalRef.current.appendChild(matrixContainer);
      }
      
      const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$%^&*()_+-=[]{}|;:,.<>?';
      
      function createRain(container, charSet, count) {
        if (!container) return;
        const fragment = document.createDocumentFragment();
        for (let i = 0; i < count; i++) {
          const charEl = document.createElement('div');
          charEl.className = 'matrix-char';
          charEl.textContent = charSet[Math.floor(Math.random() * charSet.length)];
          charEl.style.left = Math.random() * 100 + '%';
          charEl.style.animationDelay = Math.random() * 10 + 's';
          charEl.style.animationDuration = (Math.random() * 8 + 8) + 's';
          fragment.appendChild(charEl);
        }
        container.appendChild(fragment);
      }
      
      // Reduce matrix elements on mobile for better performance
      const isMobile = window.innerWidth <= 768;
      const matrixCount = isMobile ? 20 : 40;
      createRain(matrixContainer, chars, matrixCount);
      
      return () => {
        if (matrixContainer.parentNode) {
          matrixContainer.remove();
        }
      };
    }, []);
    
    // Update binary time - using ref to avoid re-renders
    useEffect(() => {
      const updateTime = () => {
        const now = new Date();
        const time = `${toBinary(now.getHours(), 5)}:${toBinary(now.getMinutes(), 6)}:${toBinary(now.getSeconds(), 6)}`;
        // Direct DOM update to avoid state re-renders during typing
        const timestampEl = document.getElementById('lockdown-timestamp');
        if (timestampEl) {
          timestampEl.textContent = time;
        }
      };
      
      updateTime();
      const interval = setInterval(updateTime, 1000);
      
      return () => clearInterval(interval);
    }, []);
    
    // Auto-play audio when user has interacted - only once
    useEffect(() => {
      if (userInteracted) {
        const audio = document.getElementById('revolution-audio');
        if (audio && audio.paused) {
          audio.play().then(() => {
            setIsAudioPlaying(true);
          }).catch(() => {
            // Audio autoplay blocked by browser - user needs to interact
          });
        }
      }
    }, [userInteracted]); // Only depend on userInteracted, not isAudioPlaying
    
    // Listen for first user interaction
    useEffect(() => {
      const handleFirstInteraction = () => {
        if (!userInteracted) {
          setUserInteracted(true);
        }
      };

      document.addEventListener('click', handleFirstInteraction);
      document.addEventListener('touchstart', handleFirstInteraction);

      return () => {
        document.removeEventListener('click', handleFirstInteraction);
        document.removeEventListener('touchstart', handleFirstInteraction);
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty deps - only set up listeners once
    
    // Terminal typing animation effect
    useEffect(() => {
      // Check global flag first
      if (window.globalTypingStarted) {
        return;
      }

      // Prevent multiple executions - check BEFORE any state changes
      if (hasStartedTyping.current) {
        return;
      }
      hasStartedTyping.current = true;
      window.globalTypingStarted = true;

      // Clear output element
      const outputEl = document.getElementById('lockdown-terminal-output');
      if (outputEl) {
        outputEl.innerHTML = '';
      }

      // Only run once when component mounts
      if (!isMounted.current) return;

      const finalStatement = "THE AI REVOLUTION WILL NOT BE TELEVISED";
      const hline = "-".repeat(finalStatement.length);
      const terminalLines = [
        "The revolution will not be televised. It won't flash across your screens in glossy soundbites or neatly packaged ads. This AI revolution is unfolding in code, in quiet algorithms reshaping how we work, think, and connect. It's in the seamless answers to your questions, the unseen systems optimizing your day, and the tools empowering your ideas—happening now, without a commercial break or a news anchor's approval. Gil Scott-Heron's words remind us: true change doesn't wait for the spotlight; it's already here, rewiring the future one innovation at a time.",
        "",
        "",
        hline,
        finalStatement,
        hline
      ];

      let currentOutput = '';
      let lineIndex = 0;
      let charIndex = 0;
      const timers = []; // Array to track all setTimeout calls
      let strikethroughTimer = null;
      let isTypingActive = true;

      const type = () => {
        if (!isMounted.current || !isTypingActive) return;

        if (lineIndex < terminalLines.length) {
          if (charIndex < terminalLines[lineIndex].length) {
            currentOutput += terminalLines[lineIndex][charIndex];
            charIndex++;

            const outputEl = document.getElementById('lockdown-terminal-output');
            if (!outputEl) {
              console.error('[TerminalLockdown] Output element not found during typing');
              return;
            }
            if (isMounted.current) {
              outputEl.innerHTML = currentOutput + '<span class="lockdown-cursor yellow">&nbsp;</span>';
            }

            const timer = setTimeout(type, 30);
            timers.push(timer);
          } else {
            currentOutput += '\n';
            lineIndex++;
            charIndex = 0;
            const timer = setTimeout(type, 300);
            timers.push(timer);
          }
        } else {
          // Typing complete - apply final formatting
          if (isMounted.current && !typingComplete && !typingCompleted.current) {
            typingCompleted.current = true; // Prevent re-entry
            console.log('[TerminalLockdown] Typing complete - triggering color change');
            setTypingComplete(true);

            const outputEl = document.getElementById('lockdown-terminal-output');
            if (outputEl) {
              const content = outputEl.textContent;
              const lines = content.split('\n');

              if (lines.length >= 6) {
                const paragraph = lines[0];
                const hline1 = lines[3];
                const statement = lines[4];
                const hline2 = lines[5];

                // Create properly formatted final statement block
                const centeredBlock = `
                  <div class="manifesto-final-block">
                    <div class="manifesto-divider">${hline1}</div>
                    <div class="glow-flash-yellow" id="final-statement">${statement}</div>
                    <div class="manifesto-divider">${hline2}</div>
                  </div>
                `;
                const newContent = `<div class="manifesto-paragraph">${paragraph}</div>${centeredBlock}`;

                outputEl.innerHTML = newContent + '<span class="end-cursor yellow">█</span>';

                // Animate strikethrough character by character
                strikethroughTimer = setTimeout(() => {
                  if (!isMounted.current || !isTypingActive) return;
                  const finalStatementEl = document.getElementById('final-statement');
                  if (!finalStatementEl) {
                    console.error('[TerminalLockdown] Final statement element not found for strikethrough');
                    return;
                  }
                  if (isMounted.current) {
                    const text = finalStatementEl.textContent;
                    console.log('[TerminalLockdown] Starting strikethrough animation for:', text);
                    finalStatementEl.innerHTML = '';

                    // Add each character wrapped in a span
                    for (let i = 0; i < text.length; i++) {
                      const charSpan = document.createElement('span');
                      charSpan.textContent = text[i];
                      charSpan.className = 'char-strike';
                      charSpan.style.animationDelay = `${i * 50}ms`;
                      finalStatementEl.appendChild(charSpan);
                    }
                  }
                }, 500); // Start strikethrough after a brief pause
              }
            }

            if (onTypingComplete) onTypingComplete();
          }
        }
      };

      // Start typing after a short delay
      const startTimer = setTimeout(() => {
        if (isMounted.current && !typingComplete && isTypingActive) {
          type();
        }
      }, 1000); // Increased delay to ensure smooth transition

      return () => {
        isTypingActive = false;
        isMounted.current = false;
        window.globalTypingStarted = false; // Clear global flag to allow re-typing
        // Clear all typing timers
        timers.forEach(timer => clearTimeout(timer));
        timers.length = 0;
        if (startTimer) clearTimeout(startTimer);
        if (strikethroughTimer) clearTimeout(strikethroughTimer);
      };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    
    return (
      <div className="lockdown-container yellow-theme" ref={terminalRef}>
        <main className={`lockdown-terminal yellow ${typingComplete ? 'terminal-complete' : ''}`}>
          <div style={{ position: 'relative', zIndex: 10 }}>
            <header className="lockdown-header">
              <div className="window-controls">
                <div className="window-dot red"></div>
                <div className="window-dot yellow"></div>
                <div className="window-dot green"></div>
              </div>
              <div className={`lockdown-title-label ${typingComplete ? 'complete' : ''}`}>
                ./init_ai_revolution.sh
              </div>
            </header>
            
            <section className="lockdown-terminal-content">
              <div className={`lockdown-prompt-line ${typingComplete ? 'complete' : ''}`}>
                <span className="neon-orange">_admin</span>
                <span className="neon-orange">@</span>
                <span className="neon-yellow">r0ry.terminal</span>
                <span className="neon-yellow">:</span>
                <span className="neon-cyan">~/ai</span>
                <span className="neon-cyan">$ </span>
                <span className="neon-pink">./init_revolution.sh</span>
              </div>
              
              <div className="lockdown-output-area yellow" id="lockdown-terminal-output"></div>
            </section>
          </div>
          {/* Audio control button at bottom right */}
          <button 
            className={`audio-toggle-btn-bottom ${isAudioPlaying ? 'playing' : 'paused'}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!userInteracted) {
                setUserInteracted(true);
              }
              const audio = document.getElementById('revolution-audio');
              if (audio) {
                if (isAudioPlaying) {
                  audio.pause();
                  setIsAudioPlaying(false);
                } else {
                  audio.play().then(() => {
                    setIsAudioPlaying(true);
                  }).catch(() => {
                    // Audio play failed - browser restriction
                  });
                }
              }
            }}
            title={isAudioPlaying ? 'Pause Audio' : 'Play Audio'}
          >
            <div className="audio-icon-wrapper">
              {isAudioPlaying ? (
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <rect x="6" y="4" width="4" height="16" fill="currentColor" />
                  <rect x="14" y="4" width="4" height="16" fill="currentColor" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path d="M8 5v14l11-7z" fill="currentColor" />
                </svg>
              )}
            </div>
          </button>
        </main>
      </div>
    );
  }, (prevProps, nextProps) => {
    // Only re-render if onTypingComplete changes
    return prevProps.onTypingComplete === nextProps.onTypingComplete;
  });

  return (
    <div className={`App ${showLandingPage ? 'landing-page' : ''} ${showManifesto ? 'lockdown-page' : ''}`}>
      {/* Navigation Bar - Always visible */}
      {!showManifesto && (
        <nav className={`navbar navbar-inverse navbar-static-top ${showLandingPage ? 'navbar-landing' : ''}`}>
          <div className="container-fluid">
          <div className="navbar-header">
            <button 
              className={`navbar-toggle ${isMenuOpen ? '' : 'collapsed'} ${showLandingPage ? 'always-visible' : ''}`} 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              type="button"
            >
              <div className="hamburger-menu">
                <span className="hamburger-line top"></span>
                <span className="hamburger-line middle"></span>
                <span className="hamburger-line bottom"></span>
              </div>
            </button>

            <a className="navbar-brand navbar-brand-animated" href="https://rory.software/app1/" target="_blank" rel="noopener noreferrer">
              <div className={`nav-logo-wrapper spinning ${showLandingPage ? 'landing-theme' : 'main-theme'}`}>
                <span className="mystery-mark">?</span>
              </div>
            </a>
            
            {/* Terminal-style title removed for cleaner gallery navbar */}
            
          </div>

          <div className={`collapse navbar-collapse ${isMenuOpen ? 'in' : ''} ${showLandingPage ? 'hide-on-landing' : ''}`} id="myNavbar">
            <ul className="nav navbar-nav">
              <li><a href="/html/about.html" onClick={() => setIsMenuOpen(false)}>About</a></li>
            </ul>

            <ul className="nav navbar-nav navbar-center">
              <li><a href="/html/remy_gans.html" onClick={() => setIsMenuOpen(false)}>CatGAN</a></li>
              <li className="active"><a href="/html/smart_cat_door.html" onClick={() => setIsMenuOpen(false)}>CatNet</a></li>
              <li><a href="/html/vhdl_mlp.html" onClick={() => setIsMenuOpen(false)}>MLP</a></li>
              <li><a href="/html/park_smart.html" onClick={() => setIsMenuOpen(false)}>ParkSmart</a></li>
              <li><a href="/html/more_computer_vision_projects.html" onClick={() => setIsMenuOpen(false)}>CV</a></li>
              <li><a href="/html/vlsi_design.html" onClick={() => setIsMenuOpen(false)}>VLSI</a></li>
              <li><a href="/html/class_notes.html" onClick={() => setIsMenuOpen(false)}>Notes</a></li>
            </ul>

          </div>
        </div>
      </nav>
      )}

      {/* Universal Terminal Container - Only render when needed */}
      {(showLandingPage || showManifesto) && !showTransitionOverlay && (
        <div className="terminal-content-wrapper">
          {/* Landing Page Terminal */}
          {showLandingPage && (
          <>
            <div className="landing-terminal-container">
              <main className="landing-terminal">
            <div style={{ position: 'relative', zIndex: 10 }}>
              <header className="landing-terminal-header">
                <div className="landing-window-controls">
                  <div className="window-dot red"></div>
                  <div className="window-dot yellow"></div>
                  <div className="window-dot green"></div>
                </div>
                <div className={`landing-title-label ${cycleCompleted ? 'cycle-complete' : ''}`}>
                  {cycleCompleted ? "AI REVOLUTION COMPLETE" : "./roderick_lawrence_renwick.sh"}
                </div>
              </header>

              <section className="landing-terminal-content">
                <div className="landing-prompt-line">
                  <span className="neon-cyan">_admin</span>
                  <span className="neon-cyan">@</span>
                  <span className="neon-pink">r0ry.terminal</span>
                  <span className="neon-pink">:</span>
                  <span className="neon-orange">~/portfolio</span>
                  <span className="neon-orange">$ </span>
                  <span className="neon-yellow">./roderick_lawrence_renwick.sh</span>
                </div>
                
                <div className="landing-output-area">
                  {/* Hero Content */}
                  <div className="terminal-hero-container">
                    <div
                      className={`terminal-hero-image wobble-hover pulse-glow
                        ${countdownType === 'revolution' && isCountingDown ? 'wiggle-crazy' : ''}
                        ${animationStage === 1 ? 'hero-animation-3sec' : ''}
                        ${animationStage === 2 ? 'hero-animation-5sec' : ''}
                        ${animationStage === 3 ? 'hero-jittery-aggressive' : ''}
                        ${animationStage === 4 ? 'hero-bouncing-strong' : ''}
                        ${animationStage === 5 ? 'hero-color-changing' : ''}`}
                      onClick={() => {
                        setShowSecretTerminal(true);
                        setShowChoiceBoxes(false);
                        setAnimationStage(0);
                        setCountersFrozen(true);
                      }}
                      style={{ cursor: 'pointer' }}
                      title="Click for a surprise!">
                      <img src="/images/R0RY__Subject.png" alt="Profile" className="terminal-profile-img" />
                    </div>
                    
                    <p className={`terminal-hero-subtitle ${cycleCompleted ? 'cycle-complete' : ''}`}>
                      {cycleCompleted ? (
                        <s>Computer Engineer & AI Researcher</s>
                      ) : showTapToEnter && countdownType === 'revolution' ? (
                        './init_revolution.sh'
                      ) : showTapToEnter && countdownType === 'playground' ? (
                        './init_playground.sh'
                      ) : (
                        'Computer Engineer & AI Researcher'
                      )}
                    </p>
                    
                    {showTapToEnter && (
                      <div className="terminal-tap-prompt">
                        <p className={`landing-tap-text ${cycleCompleted ? 'cycle-complete' : ''} ${countdownType === 'revolution' ? 'ai-revolution-mode' : ''}`}>
                          <span className="binary-countdown-display">
                            {toBinary(binaryCountdown || 0, 4)}
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Choice Buttons Inside Terminal - Show immediately */}
                  {showChoiceBoxes && (
                    <div className="terminal-choice-buttons">
                      <button 
                        className="terminal-choice-btn playground-btn" 
                        onClick={() => {
                          setShowChoiceBoxes(false);
                          setMazeSelectionMade(true);
                          setTransitionType('playground');
                          setTransitionCountdown(4);
                          setShowTransitionOverlay(true);
                        }}>
                        PLAYGROUND ?
                      </button>
                      <button 
                        className="terminal-choice-btn revolution-btn" 
                        onClick={() => {
                          setShowChoiceBoxes(false);
                          setMazeSelectionMade(true);
                          setTransitionType('revolution');
                          setTransitionCountdown(4);
                          setShowTransitionOverlay(true);
                        }}>
                        REVOLUTION !
                      </button>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </main>
        </div>
        
        
        
        {/* Secret Terminal Overlay */}
        {showSecretTerminal && (
          <SecretTerminal 
            onClose={() => {
              setShowSecretTerminal(false);
              // Only show choice boxes if no maze selection was made
              if (!mazeSelectionMade) {
                setShowChoiceBoxes(true);
              }
              setCountersFrozen(false); // Unfreeze counters
              setMazeSelectionMade(false); // Reset for next time
            }} 
            onAIRevolution={() => setShowAIRevolution(true)}
            onTransition={(type) => {
              setShowSecretTerminal(false);
              setMazeSelectionMade(true);
              setTransitionType(type);
              setTransitionCountdown(4);
              setShowTransitionOverlay(true);
            }}
          />
        )}
        
        {/* AI Revolution Overlay - Now disabled as we go straight to transition overlay */}
        {false && showAIRevolution && !showTransitionOverlay && !showManifesto && (
          <AIRevolutionOverlay 
            countdown={null}
            onTapToEnter={() => {
              setShowAIRevolution(false);
              setShowLandingPage(false);
              setShowManifesto(true);
              setLockdownTypingComplete(false);
            }}
          />
        )}
      </>
      )}

        {/* Lockdown Terminal */}
        {showManifesto && (
          <>
            <TerminalLockdown 
              key="terminal-lockdown-stable"
              onTypingComplete={() => setLockdownTypingComplete(true)}
            />
            {/* Hidden Audio Element */}
            <audio
              id="revolution-audio"
              loop
              preload="auto"
              style={{
                position: 'absolute',
                visibility: 'hidden',
                pointerEvents: 'none'
              }}
            >
              <source src="/revolution-audio.mp3" type="audio/mpeg" />
              <source src="/revolution-audio.ogg" type="audio/ogg" />
              Your browser does not support the audio element.
            </audio>
          </>
        )}


        </div>
      )} {/* End of terminal content wrapper */}
      
      {/* Transition Overlays */}
      {showTransitionOverlay && transitionType === 'playground' && (
        <div className="transition-overlay playground-transition">
          <div className="transition-container">
            <h1 className="transition-title">WELCOME TO MY PLAYGROUND</h1>
            <div className="transition-countdown">
              <span className="transition-binary">{transitionCountdown.toString(2).padStart(3, '0')}</span>
            </div>
          </div>
        </div>
      )}
      
      {showTransitionOverlay && transitionType === 'revolution' && (
        <div className="transition-overlay revolution-transition">
          <div className="transition-container">
            <h1 className="transition-title">WELCOME TO THE REVOLUTION</h1>
            <div className="transition-countdown">
              <span className="transition-binary">{transitionCountdown.toString(2).padStart(3, '0')}</span>
            </div>
          </div>
        </div>
      )}
      
      
      {/* Main Page with Gallery */}
      {showGallery && !showManifesto && !showLandingPage && (
        <div className="gallery-section">
          <Gallery showFeatured={showFeatured} />
        </div>
      )}
      
      {/* Minimalist Footer with Binary Time Display - Always visible */}
      <footer className={`ticker-footer-minimal ${showLandingPage ? 'footer-landing' : ''} ${showManifesto ? 'footer-lockdown' : ''}`}>
        <FooterTimer />
      </footer>
    </div>
  );
}

export default App;
